import { Realtime, RealtimeChannel, Message as AblyMessage, ErrorInfo } from 'ably';
import { ChatService } from '@/api/services/chatService';
import { AblyChannelUtils } from './ably-channel-utils';
import { MessageContentData, MessageInfo, SessionType } from '@/api/types/chat';
import { tabStateManager } from '@/utils/tabStateManager';
import { MessageUtils } from './message-utils';
import { EncryptionKey } from '@/api/types/user';
import ToastUtils from '../toast-utils';
import { logPrint } from '../my-utils';

/**
 * Ably 消息事件类型
 */
export enum AblyEventType {
  message = 'message',
  ack = 'ack'
}

/**
 * 群组管理通知事件（不加密，用于群组订阅管理）
 */
export enum GroupManagementEvent {
  // 发送到个人频道的，通知个人监听或取消群组的订阅
  group_created = 'group_created',
  group_rmove = 'group_rmove',
  group_enter = 'group_enter',
  // 发送到群组频道的，通知群组成员更新群组信息
  group_updated = 'group_updated', // 群信息更新
  group_disband = 'group_disband', // 群解散
  group_invite = 'group_invite',
  group_permissions_updated = 'group_permissions_updated',
}

/**
 * 群组消息通知事件（加密，显示在消息记录中）
 */
export enum GroupMessageEvent {
  group_new_create = 'group_new_create',
  group_new_invite = 'group_new_invite',
  group_info_update = 'group_info_update',
  group_dissolve = 'group_dissolve',
  group_member_removed = 'group_member_removed',
  group_member_leave = 'group_member_leave',
  group_member_added = 'group_member_added',
  group_notice_updated = 'group_notice_updated',
  group_pinned_message_add = 'group_pinned_message_add',
  group_pinned_message_delete = 'group_pinned_message_delete',
  group_mute_enabled = 'group_mute_enabled',
  group_mute_disabled = 'group_mute_disabled',
  group_screenshot_enabled = 'group_screenshot_enabled',
  group_screenshot_disabled = 'group_screenshot_disabled',
  group_send_card_enabled = 'group_send_card_enabled',
  group_send_card_disabled = 'group_send_card_disabled',
  group_send_image_enabled = 'group_send_image_enabled',
  group_send_image_disabled = 'group_send_image_disabled',
  group_send_video_enabled = 'group_send_video_enabled',
  group_send_video_disabled = 'group_send_video_disabled',
  group_send_file_enabled = 'group_send_file_enabled',
  group_send_file_disabled = 'group_send_file_disabled'
}

/**
 * 所有群组通知事件类型
 */
export type GroupNotificationEvent = GroupManagementEvent | GroupMessageEvent;

/**
 * 获取群组管理通知事件数组
 */
export const GROUP_MANAGEMENT_TYPES = Object.values(GroupManagementEvent);

/**
 * 获取群组消息通知事件数组  
 */
export const GROUP_MESSAGE_TYPES = Object.values(GroupMessageEvent);

/**
 * 获取所有群组通知事件类型数组
 */
export const GROUP_NOTIFICATION_TYPES = [...GROUP_MANAGEMENT_TYPES, ...GROUP_MESSAGE_TYPES];

/**
 * 发送消息参数
 */
export interface SendMessageParams {
  conversationId: string;
  groupId?: string;
  receiverId?: string;
  receiverHandle?: number;
  contentData: MessageContentData;
  sessionType: SessionType;
}

/**
 * 群组管理通知数据（不加密）
 */
export interface GroupManagementNotificationData {
  type: GroupManagementEvent;
  groupId: string;
  data?: any; // 额外的事件数据
}

/**
 * 群组消息通知数据（加密）
 */
export interface GroupMessageNotificationData {
  messageInfo: MessageInfo;
}

/**
 * 消息确认数据
 */
export interface AckData {
  conversation_id: string;
  message_id: string;
  session_type: SessionType;
}

/**
 * Ably 连接管理器
 * 负责连接管理、频道订阅和消息发送
 */
export class AblyManager {
  private static instance: AblyManager;
  private static realtime: Realtime | null = null;
  private static isConnected = false;
  private static isInitialized = false;
  private userChannel: RealtimeChannel | null = null;
  private ackChannel: RealtimeChannel | null = null;
  private groupChannels: Map<string, RealtimeChannel> = new Map();

  // 事件监听器
  private messageListeners: Array<(message: MessageInfo) => void> = [];
  private ackListeners: Array<(ackData: { conversation_id: string, message_ids: string[] }) => void> = [];
  private connectionListeners: Array<(isConnected: boolean) => void> = [];
  // 群组通知监听器
  private groupManagementListeners: Array<(data: GroupManagementNotificationData) => void> = [];
  private groupMessageListeners: Array<(data: GroupMessageNotificationData) => void> = [];

  private constructor() { }

  /**
   * 获取单例实例
   */
  static getInstance(): AblyManager {
    if (!AblyManager.instance) {
      AblyManager.instance = new AblyManager();
    }
    return AblyManager.instance;
  }

  /**
   * 初始化 Ably 连接
   */
  async initialize(): Promise<boolean> {
    if (AblyManager.isInitialized) {
      console.log('Ably 已初始化，跳过重复初始化');
      return AblyManager.isConnected;
    }

    try {
      // 创建 Ably 实例，使用 authCallback 方式
      AblyManager.realtime = new Realtime({
        authCallback: async (_tokenParams, callback) => {
          try {
            const tokenResult = await ChatService.getAblyToken();
            if (tokenResult && tokenResult.token) {
              const tokenRequest = {
                ...tokenResult.token,
                clientId: tokenResult.token.clientId?.toString()
              };

              logPrint('tokenRequest', tokenRequest);

              callback(null, tokenRequest);
            } else {
              console.error('Ably token 结果无效:', tokenResult);
              callback(new ErrorInfo('获取到的 Ably token 无效', 40170, 500), null);
            }
          } catch (err: any) {
            console.error('获取 Ably token 失败:', err);
            callback(err instanceof ErrorInfo ? err : new ErrorInfo('获取 Ably token 失败', 40170, 500, err), null);
            return;
          }
        },
        disconnectedRetryTimeout: 5000,
        suspendedRetryTimeout: 5000,
      });

      // 监听连接状态
      AblyManager.realtime.connection.on((stateChange) => {
        if (stateChange.current === 'connected') {
          console.log('Ably 连接成功');
          AblyManager.isConnected = true;
          this.notifyConnectionListeners(true);
          this.subscribeToUserChannel();
          this.subscribeToAckChannel();
        }
        if (stateChange.current === 'disconnected') {
          console.log('Ably 连接断开');
          AblyManager.isConnected = false;
          this.notifyConnectionListeners(false);
        }
        if (stateChange.current === 'failed') {
          console.error('Ably 连接失败:', stateChange.reason);
          AblyManager.isConnected = false;
          this.notifyConnectionListeners(false);
        }
      });

      // 连接到 Ably
      await AblyManager.realtime.connect();
      AblyManager.isInitialized = true;
      return AblyManager.isConnected;
    } catch (error) {
      console.error('Ably 初始化失败:', error);
      AblyManager.isInitialized = false;
      return false;
    }
  }

  /**
   * 订阅用户handle频道（接收私聊消息）
   */
  private async subscribeToUserChannel(): Promise<void> {
    if (!AblyManager.realtime) return;

    try {
      // 获取当前用户信息
      const authData = tabStateManager.getAuthData();
      if (!authData?.userInfo?.handle) {
        console.error('无法获取用户句柄，跳过用户频道订阅');
        return;
      }

      const channelName = AblyChannelUtils.getCurrentUserChannelId(authData.userInfo.handle.toString());
      this.userChannel = AblyManager.realtime.channels.get(channelName);

      // 订阅消息
      this.userChannel.subscribe(AblyEventType.message, (message: AblyMessage) => {
        this.handleIncomingMessage(message);
      });

      // 订阅群 创建/进入/移除 的通知
      this.userChannel.subscribe([
        GroupManagementEvent.group_created,
        GroupManagementEvent.group_rmove,
        GroupManagementEvent.group_enter,
      ], (message: AblyMessage) => {
        this.handleGroupNotification(message)
      });

      logPrint(`已订阅用户频道: ${channelName}`);
    } catch (error) {
      console.error('订阅用户频道失败:', error);
    }
  }

  /**
   * 订阅消息确认频道
   */
  private async subscribeToAckChannel(): Promise<void> {
    if (!AblyManager.realtime) return;

    try {
      const channelName = AblyChannelUtils.getAckChannelId();
      this.ackChannel = AblyManager.realtime.channels.get(channelName);

      // 订阅消息确认
      this.ackChannel.subscribe(AblyEventType.ack, (message: AblyMessage) => {
        this.handleAckMessage(message);
      });

      logPrint(`已订阅确认频道: ${channelName}`);
    } catch (error) {
      console.error('订阅确认频道失败:', error);
    }
  }

  /**
   * 订阅群组频道
   */
  async subscribeToGroupChannel(groupId: string): Promise<void> {
    if (!AblyManager.realtime) return;

    try {
      const channelName = AblyChannelUtils.getGroupChannelId(groupId);

      // 避免重复订阅
      if (this.groupChannels.has(groupId)) {
        logPrint(`群组频道 ${channelName} 已订阅`);
        return;
      }

      const channel = AblyManager.realtime.channels.get(channelName);
      this.groupChannels.set(groupId, channel);

      // 订阅群组消息
      channel.subscribe(AblyEventType.message, (message: AblyMessage) => {
        this.handleIncomingMessage(message);
      });

      // 订阅群组所有消息的通知
      channel.subscribe(GROUP_NOTIFICATION_TYPES, (message: AblyMessage) => {
        this.handleGroupNotification(message);
      });

      logPrint(`已订阅群组频道: ${channelName}`);
    } catch (error) {
      console.error(`订阅群组频道失败 (${groupId}):`, error);
    }
  }

  /**
   * 取消订阅群组频道
   */
  async unsubscribeFromGroupChannel(groupId: string): Promise<void> {
    try {
      const channel = this.groupChannels.get(groupId);
      if (channel) {
        await channel.unsubscribe();
        await channel.detach();
        this.groupChannels.delete(groupId);
        logPrint(`已取消订阅群组频道: ${groupId}`);
      }
    } catch (error) {
      console.error(`取消订阅群组频道失败 (${groupId}):`, error);
    }
  }

  // 缓存本次收到的 messageId
  private cachedMessageIds: Set<string> = new Set();
  private encryptionKey: EncryptionKey | null = null;

  /**
   * 处理收到的消息
   */
  private async handleIncomingMessage(message: AblyMessage): Promise<void> {
    logPrint('收到 Ably 消息:', message);
    try {
      let messageInfoRep = message.data;

      // 检查消息是否存在
      if (this.cachedMessageIds.has(messageInfoRep.message_id)) {
        logPrint(`消息 ${messageInfoRep.message_id} 已存在，跳过添加`);
        return;
      }
      this.cachedMessageIds.add(messageInfoRep.message_id);

      // 构造 MessageInfo 对象
      let messageInfo: MessageInfo = {
        message_id: messageInfoRep.message_id,
        session_type: messageInfoRep.session_type,
        conversation_id: messageInfoRep.conversation_id,
        sender_id: messageInfoRep.sender_id,
        receiver_id: messageInfoRep.receiver_id,
        message_type: messageInfoRep.message_type,
        content: messageInfoRep.content,
        iv: messageInfoRep.iv,
        auth_tag: messageInfoRep.auth_tag,
        key_id: messageInfoRep.key_id || '',
        message_id_time: messageInfoRep.message_id_time,
        receiver_time: messageInfoRep.receiver_time,
        deleted_at: null,
        attachments: messageInfoRep.attachments,
        messageType: undefined,
        // ack: false,
        // read: false,
        decryptedContentData: undefined,
      };

      if (!this.encryptionKey) {
        this.encryptionKey = tabStateManager.getEncryptionKey();
      }

      if (this.encryptionKey) {
        // 解密消息内容
        const decryptedContent = await MessageUtils.decryptMessageContent(messageInfo, this.encryptionKey);
        const contentData = JSON.parse(decryptedContent) as MessageContentData;

        messageInfo.content = decryptedContent;
        messageInfo.decryptedContentData = contentData;

        messageInfo.messageType = contentData.messageType;
        messageInfo.sender_id = contentData.senderId;
        messageInfo.receiver_time = messageInfo.receiver_time || Date.parse(contentData.timestamp);
      }

      // 通知消息监听器
      this.messageListeners.forEach(listener => {
        try {
          listener(messageInfo);
        } catch (error) {
          console.error('消息监听器处理失败:', error);
        }
      });
    } catch (error) {
      console.error('处理收到的消息失败:', error);
    }
  }

  /**
   * 处理群组通知事件
   * @param message Ably消息
   * @returns 是否处理了群组通知事件
   */
  private async handleGroupNotification(message: AblyMessage): Promise<boolean> {
    logPrint('收到群组通知:', message);

    const eventName = message.name;
    const data = message.data;

    if (!GROUP_NOTIFICATION_TYPES.includes(eventName as GroupNotificationEvent)) {
      return false;
    }

    // 判断是管理通知还是消息通知
    if (GROUP_MANAGEMENT_TYPES.includes(eventName as GroupManagementEvent)) {
      // 处理不加密的管理通知
      return this.handleGroupManagementNotification(eventName as GroupManagementEvent, data);
    } else if (GROUP_MESSAGE_TYPES.includes(eventName as GroupMessageEvent)) {
      // 处理加密的消息通知
      return await this.handleGroupMessageNotification(eventName as GroupMessageEvent, data);
    }

    return false;
  }

  /**
   * 处理群组管理通知（不加密）
   */
  private handleGroupManagementNotification(eventName: GroupManagementEvent, data: any): boolean {
    logPrint('处理群组管理通知(不加密):', eventName, data);

    if (data?.group_id) {
      // 构造管理通知数据
      const notificationData: GroupManagementNotificationData = {
        type: eventName,
        groupId: data.group_id,
        data: data
      };

      // 通知外部管理通知监听器
      this.notifyGroupManagementListeners(notificationData);
      return true;
    }

    return false;
  }

  /**
   * 处理群组消息通知（加密）
   */
  private async handleGroupMessageNotification(eventName: GroupMessageEvent, messageInfoRep: any): Promise<boolean> {
    logPrint('处理群组消息通知(加密消息):', eventName, messageInfoRep);

    // 检查消息是否存在
    if (this.cachedMessageIds.has(messageInfoRep.message_id)) {
      logPrint(`消息 ${messageInfoRep.message_id} 已存在，跳过添加`);
      return false;
    }
    this.cachedMessageIds.add(messageInfoRep.message_id);

    // 构造 MessageInfo 对象
    let messageInfo: MessageInfo = {
      message_id: messageInfoRep.message_id,
      session_type: messageInfoRep.session_type,
      conversation_id: messageInfoRep.conversation_id,
      sender_id: messageInfoRep.sender_id,
      receiver_id: messageInfoRep.receiver_id,
      message_type: messageInfoRep.message_type,
      content: messageInfoRep.content,
      iv: messageInfoRep.iv,
      auth_tag: messageInfoRep.auth_tag,
      key_id: messageInfoRep.key_id || '',
      message_id_time: messageInfoRep.message_id_time,
      receiver_time: messageInfoRep.receiver_time,
      attachments: messageInfoRep.attachments,
      messageType: messageInfoRep.message_type,
      ably_name: eventName,
      decryptedContentData: undefined,
      deleted_at: undefined
    };

    if (!this.encryptionKey) {
      this.encryptionKey = tabStateManager.getEncryptionKey();
    }

    if (this.encryptionKey) {
      try {
        // 解密消息数据
        const decryptedData = await MessageUtils.decryptMessageContent(messageInfo, this.encryptionKey);
        const contentData = JSON.parse(decryptedData) as MessageContentData;

        messageInfo.decryptedContentData = contentData;
        messageInfo.messageType = contentData.messageType;
        messageInfo.sender_id = contentData.senderId;
        messageInfo.receiver_time = messageInfo.receiver_time || Date.parse(contentData.timestamp);

        logPrint("组装后的群组消息通知数据:", messageInfo);

        // 构造消息通知数据
        const messageNotificationData: GroupMessageNotificationData = {
          messageInfo: messageInfo
        };

        // 通知外部消息通知监听器
        this.notifyGroupMessageListeners(messageNotificationData);
        return true;
      } catch (error) {
        console.error('解密群组消息通知失败:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * 处理消息确认
   */
  private handleAckMessage(message: AblyMessage): void {
    try {
      const ackData = message.data;
      logPrint('收到消息确认:', ackData);

      // 通知确认监听器
      this.ackListeners.forEach(listener => {
        try {
          listener(ackData);
        } catch (error) {
          console.error('确认监听器处理失败:', error);
        }
      });
    } catch (error) {
      console.error('处理消息确认失败:', error);
    }
  }

  /**
   * 发送消息
   */
  async sendMessage(messageData: SendMessageParams): Promise<boolean> {
    if (!AblyManager.realtime) {
      await this.initialize();
    }

    if (!AblyManager.isConnected) {
      console.error('Ably 未连接，无法发送消息');
      return false;
    }

    try {
      // 获取当前用户信息
      const authData = tabStateManager.getAuthData();
      if (!authData?.userInfo?.handle) {
        throw new Error('无法获取当前用户信息');
      }

      // 加密消息内容
      if (!this.encryptionKey) {
        this.encryptionKey = tabStateManager.getEncryptionKey();
      }

      if (this.encryptionKey) {
        const encryptedData = await MessageUtils.encryptMessageContent(messageData.contentData, this.encryptionKey);

        // 构造 Ably 消息数据
        const ablyMessageData = {
          conversation_id: messageData.conversationId,
          session_type: messageData.sessionType,
          message_id: messageData.contentData.id,
          sender_id: authData.userId,
          receiver_id: messageData.sessionType === 'group' ? messageData.groupId : messageData.receiverId,
          message_type: messageData.contentData.messageType,
          content: encryptedData.content,
          algorithm: encryptedData.algorithm,
          iv: encryptedData.iv,
          key_id: encryptedData.key_id,
          auth_tag: encryptedData.auth_tag,
          timestamp: new Date().toISOString(),
        };

        // 确定发送频道
        let targetChannel: RealtimeChannel;

        let channelName;
        if (messageData.sessionType === 'group') {
          // 群聊消息：发送到群组频道
          channelName = AblyChannelUtils.getGroupChannelId(messageData.groupId ?? "");
          targetChannel = AblyManager.realtime!.channels.get(channelName);
        } else {
          // 私聊消息：发送到目标用户的handle频道
          if (!messageData.receiverHandle) {
            throw new Error('私聊消息必须指定接收者ID');
          }
          channelName = AblyChannelUtils.getTargetUserChannelId(messageData.receiverHandle?.toString() || '0');
          targetChannel = AblyManager.realtime!.channels.get(channelName);
        }

        // 发送消息
        await targetChannel.publish(AblyEventType.message, ablyMessageData);
        logPrint('消息发送成功:', channelName, ablyMessageData);
      } else {
        console.error('无法获取加密密钥，无法发送消息');
        ToastUtils.showToastFail('当前登录状态异常，请退出重新登录');
        return false;
      }
      return true;
    } catch (error) {
      console.error('发送消息失败:', error);
      return false;
    }
  }

  /**
   * 发送群组管理通知（不加密的简单通知）
   * 用于群组创建、加入、移除等管理操作
   * @param groupId 群组ID
   * @param eventType 管理事件类型
   * @param eventData 事件数据
   * @param userHandles 目标用户句柄列表（可选，用于批量通知）
   * @returns 是否发送成功
   */
  async sendGroupManagementNotification(
    groupId: string,
    eventType: GroupManagementEvent,
    eventData: Record<string, any>,
    userHandles?: number[]
  ): Promise<boolean> {
    if (!AblyManager.realtime) {
      await this.initialize();
    }

    if (!AblyManager.isConnected) {
      console.error('Ably 未连接，无法发送群组管理通知');
      return false;
    }

    try {
      logPrint('发送群组管理通知:', { groupId, eventType, eventData, userHandles });

      // 如果指定了用户句柄，发送到个人频道
      if (userHandles && userHandles.length > 0) {
        const successCount = await this.notifyUsersToListenGroup(
          eventType as 'group_created' | 'group_rmove' | 'group_enter',
          groupId,
          userHandles
        );
        return successCount > 0;
      } else {
        // 发送到群组频道
        const channelName = AblyChannelUtils.getGroupChannelId(groupId);
        const targetChannel = AblyManager.realtime!.channels.get(channelName);
        await targetChannel.publish(eventType, { group_id: groupId, ...eventData });
        logPrint('群组管理通知发送成功:', channelName, eventType);
        return true;
      }
    } catch (error) {
      console.error('发送群组管理通知失败:', error);
      return false;
    }
  }

  /**
   * 发送群组消息通知（使用加密的标准消息格式）
   * 用于群组消息记录中显示的通知
   * @param messageData 消息数据
   * @param eventType 消息事件类型
   * @returns 是否发送成功
   */
  async sendGroupMessageNotification(
    messageData: SendMessageParams,
    eventType: GroupMessageEvent
  ): Promise<boolean> {
    if (!AblyManager.realtime) {
      await this.initialize();
    }

    if (!AblyManager.isConnected) {
      console.error('Ably 未连接，无法发送群组消息通知');
      return false;
    }

    try {
      logPrint('发送群组消息通知:', { eventType, messageData });

      // 获取当前用户信息
      const authData = tabStateManager.getAuthData();
      if (!authData?.userInfo?.handle) {
        throw new Error('无法获取当前用户信息');
      }

      // 加密消息内容
      if (!this.encryptionKey) {
        this.encryptionKey = tabStateManager.getEncryptionKey();
      }

      if (this.encryptionKey) {
        const encryptedData = await MessageUtils.encryptMessageContent(messageData.contentData, this.encryptionKey);

        // 构造 Ably 消息数据（和普通消息格式一致）
        const ablyMessageData = {
          conversation_id: messageData.conversationId,
          session_type: messageData.sessionType,
          message_id: crypto.randomUUID(),
          sender_id: authData.userId,
          receiver_id: messageData.groupId,
          message_type: messageData.contentData.messageType,
          content: encryptedData.content,
          algorithm: encryptedData.algorithm,
          iv: encryptedData.iv,
          key_id: encryptedData.key_id,
          auth_tag: encryptedData.auth_tag,
          timestamp: new Date().toISOString(),
        };

        // 确定发送频道（群组频道）
        const channelName = AblyChannelUtils.getGroupChannelId(messageData.groupId ?? "");
        const targetChannel = AblyManager.realtime!.channels.get(channelName);

        // 发送群组消息通知，使用具体的事件类型作为消息名称
        await targetChannel.publish(eventType, ablyMessageData);
        logPrint('群组消息通知发送成功:', channelName, eventType, ablyMessageData);
      } else {
        console.error('无法获取加密密钥，无法发送群组消息通知');
        ToastUtils.showToastFail('当前登录状态异常，请退出重新登录');
        return false;
      }
      return true;
    } catch (error) {
      console.error('发送群组消息通知失败:', error);
      return false;
    }
  }

  /**
   * 发送消息确认
   * @param ackDatas 
   * @returns 
   */
  async sendAckMessages(ackDatas: AckData[]): Promise<boolean> {
    if (!AblyManager.realtime || !AblyManager.isConnected) {
      console.error('Ably 未连接，无法发送确认');
      return false;
    }

    try {
      const channelName = AblyChannelUtils.getAckChannelId();
      const channel = AblyManager.realtime.channels.get(channelName);

      for (const ackData of ackDatas) {
        await channel.publish(AblyEventType.ack, ackData);
      }
      logPrint('消息确认发送成功:', ackDatas);
      return true;
    } catch (error) {
      console.error('发送消息确认失败:', error);
      return false;
    }
  }

  /**
   * 批量通知用户监听群组（群组创建通知）
   * @param groupType 群组通知类型
   * @param groupId 群组ID
   * @param userHandles 用户句柄列表
   * @returns 成功发送的用户数量
   */
  async notifyUsersToListenGroup(
    groupType: 'group_created' | 'group_rmove' | 'group_enter',
    groupId: string,
    userHandles: number[]
  ): Promise<number> {
    if (!AblyManager.realtime || !AblyManager.isConnected) {
      console.error('Ably 未连接，无法发送群组创建通知');
      return 0;
    }

    if (!groupId || !userHandles || userHandles.length === 0) {
      console.error('群组ID或用户句柄列表无效');
      return 0;
    }

    let successCount = 0;


    try {
      // 并发发送通知给所有用户
      const sendPromises = userHandles.map(async (userHandle) => {
        try {
          const channelName = AblyChannelUtils.getTargetUserChannelId(userHandle.toString());
          const targetChannel = AblyManager.realtime!.channels.get(channelName);

          const notificationData = { group_id: groupId };
          await targetChannel.publish(groupType, notificationData);
          logPrint(`群组创建通知发送成功 - 用户: ${userHandle}, 群组: ${groupId}`);
          return true;
        } catch (error) {
          console.error(`群组创建通知发送失败 - 用户: ${userHandle}, 群组: ${groupId}:`, error);
          return false;
        }
      });

      // 等待所有发送操作完成
      const results = await Promise.all(sendPromises);
      successCount = results.filter(result => result === true).length;

      logPrint(`群组创建通知批量发送完成 - 成功: ${successCount}/${userHandles.length}, 群组: ${groupId}`);
      return successCount;
    } catch (error) {
      console.error('批量发送群组创建通知失败:', error);
      return successCount;
    }
  }

  /**
   * 添加消息监听器
   */
  addMessageListener(listener: (message: MessageInfo) => void): void {
    this.messageListeners.push(listener);
  }
  /**
   * 移除消息监听器
   */
  removeMessageListener(listener: (message: MessageInfo) => void): void {
    const index = this.messageListeners.indexOf(listener);
    if (index > -1) {
      this.messageListeners.splice(index, 1);
    }
  }

  /**
   * 添加确认监听器
   */
  addAckListener(listener: (ackData: { conversation_id: string, message_ids: string[] }) => void): void {
    this.ackListeners.push(listener);
  }
  /**
   * 移除确认监听器
   */
  removeAckListener(listener: (ackData: { conversation_id: string, message_ids: string[] }) => void): void {
    const index = this.ackListeners.indexOf(listener);
    if (index > -1) {
      this.ackListeners.splice(index, 1);
    }
  }

  /**
   * 添加连接状态监听器
   */
  addConnectionListener(listener: (isConnected: boolean) => void): void {
    this.connectionListeners.push(listener);
  }
  /**
   * 移除连接状态监听器
   */
  removeConnectionListener(listener: (isConnected: boolean) => void): void {
    const index = this.connectionListeners.indexOf(listener);
    if (index > -1) {
      this.connectionListeners.splice(index, 1);
    }
  }
  /**
   * 通知连接状态监听器
   */
  private notifyConnectionListeners(isConnected: boolean): void {
    this.connectionListeners.forEach(listener => {
      try {
        listener(isConnected);
      } catch (error) {
        console.error('连接状态监听器处理失败:', error);
      }
    });
  }


  /**
   * 添加群组管理通知监听器
   */
  addGroupManagementListener(listener: (data: GroupManagementNotificationData) => void): void {
    this.groupManagementListeners.push(listener);
  }
  /**
   * 移除群组管理通知监听器
   */
  removeGroupManagementListener(listener: (data: GroupManagementNotificationData) => void): void {
    const index = this.groupManagementListeners.indexOf(listener);
    if (index > -1) {
      this.groupManagementListeners.splice(index, 1);
    }
  }
  /**
    * 通知群组管理通知监听器
    */
  private notifyGroupManagementListeners(data: GroupManagementNotificationData): void {
    this.groupManagementListeners.forEach(listener => {
      try {
        listener(data);
      } catch (error) {
        console.error('群组管理通知监听器处理失败:', error);
      }
    });
  }

  /**
   * 添加群组消息通知监听器
   */
  addGroupMessageListener(listener: (data: GroupMessageNotificationData) => void): void {
    this.groupMessageListeners.push(listener);
  }
  /**
   * 移除群组消息通知监听器
   */
  removeGroupMessageListener(listener: (data: GroupMessageNotificationData) => void): void {
    const index = this.groupMessageListeners.indexOf(listener);
    if (index > -1) {
      this.groupMessageListeners.splice(index, 1);
    }
  }
  /**
   * 通知群组消息通知监听器
   */
  private notifyGroupMessageListeners(data: GroupMessageNotificationData): void {
    this.groupMessageListeners.forEach(listener => {
      try {
        listener(data);
      } catch (error) {
        console.error('群组消息通知监听器处理失败:', error);
      }
    });
  }

  /**
   * 断开连接
   */
  async disconnect(): Promise<void> {
    try {
      console.log('Ably 开始断开连接');

      // 清理群组频道
      for (const [, channel] of this.groupChannels) {
        await channel.unsubscribe();
        await channel.detach();
      }
      this.groupChannels.clear();

      // 清理用户频道
      if (this.userChannel) {
        await this.userChannel.unsubscribe();
        await this.userChannel.detach();
        this.userChannel = null;
      }

      // 清理确认频道
      if (this.ackChannel) {
        await this.ackChannel.unsubscribe();
        await this.ackChannel.detach();
        this.ackChannel = null;
      }

      // 关闭连接
      if (AblyManager.realtime) {
        await AblyManager.realtime.close();
        AblyManager.realtime = null;
      }

      AblyManager.isConnected = false;
      AblyManager.isInitialized = false;

      // 清空监听器
      this.messageListeners = [];
      this.ackListeners = [];
      this.connectionListeners = [];
      this.groupManagementListeners = [];
      this.groupMessageListeners = [];

      console.log('Ably 连接已断开');
    } catch (error) {
      console.error('断开 Ably 连接失败:', error);
    }
  }

  /**
   * 获取连接状态
   */
  getConnectionStatus(): boolean {
    return AblyManager.isConnected;
  }

  /**
   * 重新连接
   */
  async reconnect(): Promise<boolean> {
    console.log('正在重新连接 Ably...');
    await this.disconnect();
    return await this.initialize();
  }

}

export default AblyManager;
